import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardHeader, Button, Input } from '../components/ui'
import { PageHeader } from '../../ui'
import { useIXVerifyAuth } from '../contexts'
import { supabase } from '../../services/supabase'
import type { AuditAction } from '../lib/auditLog'

interface AuditLog {
  id: string
  site_id: string | null
  user_id: string | null
  action: AuditAction
  target_type: string
  target_id: string | null
  details: Record<string, unknown> | null
  ip_address: string | null
  user_agent: string | null
  created_at: string
  user?: {
    email: string
    full_name: string | null
  }
}

const ACTION_LABELS: Record<AuditAction, string> = {
  login_success: 'Login Success',
  login_failed: 'Login Failed',
  logout: 'Logout',
  session_expired: 'Session Expired',
  password_changed: 'Password Changed',
  account_locked: 'Account Locked',
  account_unlocked: 'Account Unlocked',
  user_invited: 'User Invited',
  user_created: 'User Created',
  user_role_changed: 'Role Changed',
  user_deleted: 'User Deleted',
  verification_created: 'Verification Created',
  verification_viewed: 'Verification Viewed',
  verification_exported: 'Verification Exported',
}

// Action-category colors are domain data — hues kept, dark pairs added per spec
const ACTION_COLORS: Record<string, string> = {
  login_success: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  login_failed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  logout: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  session_expired: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  password_changed: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  account_locked: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  account_unlocked: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  user_invited: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  user_created: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  user_role_changed: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  user_deleted: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  verification_created: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  verification_viewed: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  verification_exported: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
}

export function AuditLogs() {
  const { user } = useIXVerifyAuth()
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [exporting, setExporting] = useState(false)

  // Filters
  const [startDate, setStartDate] = useState(() => {
    const date = new Date()
    date.setDate(date.getDate() - 7) // Default to last 7 days
    return date.toISOString().split('T')[0]
  })
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0]
  })
  const [actionFilter, setActionFilter] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')

  // Check if user can view audit logs (admin or superadmin)
  const canViewAuditLogs = user?.ixverify_role === 'admin' || user?.ixverify_role === 'superadmin'

  const fetchAuditLogs = useCallback(async () => {
    if (!user || !canViewAuditLogs) return

    setLoading(true)
    setError('')

    try {
      // Build query - note: audit_logs table isn't in types
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let query = (supabase as any)
        .from('audit_logs')
        .select(`
          *,
          user:users(email, full_name)
        `)
        .order('created_at', { ascending: false })
        .limit(500)

      // Filter by site for non-superadmins
      if (user.role !== 'superadmin' && user.site_id) {
        query = query.eq('site_id', user.site_id)
      }

      // Date filters
      if (startDate) {
        query = query.gte('created_at', `${startDate}T00:00:00`)
      }
      if (endDate) {
        query = query.lte('created_at', `${endDate}T23:59:59`)
      }

      // Action filter
      if (actionFilter !== 'all') {
        query = query.eq('action', actionFilter)
      }

      const { data, error: fetchError } = await query

      if (fetchError) throw fetchError

      setLogs(data || [])
    } catch (err) {
      console.error('Error fetching audit logs:', err)
      setError('Failed to load audit logs')
    } finally {
      setLoading(false)
    }
  }, [user, canViewAuditLogs, startDate, endDate, actionFilter])

  useEffect(() => {
    fetchAuditLogs()
  }, [fetchAuditLogs])

  // Filter logs by search query (client-side)
  const filteredLogs = logs.filter((log) => {
    if (!searchQuery) return true
    const search = searchQuery.toLowerCase()
    return (
      log.user?.email?.toLowerCase().includes(search) ||
      log.user?.full_name?.toLowerCase().includes(search) ||
      log.action.toLowerCase().includes(search) ||
      log.target_type?.toLowerCase().includes(search) ||
      JSON.stringify(log.details)?.toLowerCase().includes(search)
    )
  })

  const exportToCSV = async () => {
    setExporting(true)
    try {
      const headers = ['Timestamp', 'User', 'Action', 'Target Type', 'Target ID', 'Details', 'IP Address']
      const rows = filteredLogs.map((log) => [
        new Date(log.created_at).toLocaleString(),
        log.user?.email || 'System',
        ACTION_LABELS[log.action] || log.action,
        log.target_type,
        log.target_id || '',
        log.details ? JSON.stringify(log.details) : '',
        log.ip_address || '',
      ])

      const csvContent = [
        headers.join(','),
        ...rows.map((row) =>
          row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
        ),
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `audit-logs-${startDate}-to-${endDate}.csv`
      link.click()
    } catch (err) {
      console.error('Error exporting CSV:', err)
    } finally {
      setExporting(false)
    }
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleString()
  }

  const formatDetails = (details: Record<string, unknown> | null) => {
    if (!details) return null
    return Object.entries(details)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ')
  }

  if (!canViewAuditLogs) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <PageHeader title="Audit Logs" subtitle="CFR 21 Part 11 Compliance Audit Trail" />
        <Card>
          <CardContent className="py-12 text-center">
            <svg className="w-12 h-12 mx-auto text-gray-300 mb-4 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <p className="text-gray-500 dark:text-gray-400">You don't have permission to view audit logs.</p>
            <p className="text-sm text-gray-400 mt-1 dark:text-gray-500">Contact an administrator for access.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <PageHeader
        title="Audit Logs"
        subtitle="CFR 21 Part 11 Compliance Audit Trail"
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={exportToCSV}
            disabled={exporting || filteredLogs.length === 0}
          >
            {exporting ? (
              <>
                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2" />
                Exporting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export CSV
              </>
            )}
          </Button>
        }
      />

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Start Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">End Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Action Type</label>
              <select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              >
                <option value="all">All Actions</option>
                {Object.entries(ACTION_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Search</label>
              <Input
                type="text"
                placeholder="Search logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Activity Log</h2>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {filteredLogs.length} {filteredLogs.length === 1 ? 'entry' : 'entries'}
            </span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400" />
            </div>
          ) : error ? (
            <div className="py-12 text-center text-red-600 dark:text-red-400">{error}</div>
          ) : filteredLogs.length === 0 ? (
            <div className="py-12 text-center text-gray-500 dark:text-gray-400">
              <svg className="w-10 h-10 mx-auto text-gray-300 mb-2 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              No audit logs found for the selected filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200 dark:bg-gray-800/50 dark:border-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
                      Timestamp
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
                      User
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
                      Action
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
                      Target
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredLogs.map((log) => (
                    <tr key={log.id} className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatTimestamp(log.created_at)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {log.user?.full_name || log.user?.email || 'System'}
                        </div>
                        {log.user?.email && log.user?.full_name && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">{log.user.email}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 text-xs font-medium rounded-full ${ACTION_COLORS[log.action] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}`}>
                          {ACTION_LABELS[log.action] || log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        <div>{log.target_type}</div>
                        {log.target_id && (
                          <div className="text-xs text-gray-400 font-mono dark:text-gray-500">{log.target_id.substring(0, 8)}...</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate dark:text-gray-400">
                        {formatDetails(log.details)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
